import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { Grid, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import ImageInput from "../../../../../../../../../components/Form/ImageInput";
import Input from "../../../../../../../../../components/Form/Input";
import Select from "../../../../../../../../../components/Form/Select";
import Modal from "../../../../../../../../../components/Generic/Modal";
import useYupValidation from "../../../../../../../../../hooks/useYupValidation";
import { Brand } from "../../../../../../../../../models/brand";
import { Logo } from "../../../../../../../../../models/company";
import { Status } from "../../../../../../../../../util/enums";
import useAction from "../../hooks/useAction";
import { PdfView } from "../PdfView";
import useStyles, { FieldsGrid, Label } from "./styles";

interface BrandModalProps {
  onSubmit: (brand: Brand) => void;
  onDelete: (ids: number[]) => void;
  isLoading?: boolean;
}

export interface BrandModalHandle {
  open: (brand?: Brand, view?: boolean) => void;
  close: () => void;
}

const CreateModal: ForwardRefRenderFunction<BrandModalHandle, BrandModalProps> = (props, ref) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { onSubmit, onDelete, isLoading = false } = props;

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [brand, setBrand] = useState<Brand>();
  const [view, setView] = useState<boolean>(false);
  const [error, setError] = useState<yup.ValidationError[]>([]);
  const [buffer, setBuffer] = useState<Blob>();
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout>();
  const { getErrorByField, fieldHasError } = useYupValidation(error);

  const { useGetPreviewLabel } = useAction();
  const { mutate: getPreview, isLoading: isLoadingPreview } = useGetPreviewLabel(setBuffer);

  useEffect(() => {
    if (!openModal && !buffer) {
      getPreview(brand);
    }

    if (openModal) getPreview(brand);
  }, [brand?.logo, openModal]);

  const onClearFields = () => {
    setBrand(undefined);
    setView(false);
    setError([]);
    setBuffer(undefined);
  };

  const onCloseModal = () => {
    onClearFields();
    setOpenModal(false);
  };

  const onOpenModal = (data?: Brand, view = false) => {
    setOpenModal(true);
    setView(view);
    setBrand(data || { status: Status.ACTIVE });
  };

  const handleNameChange = (name: string) => {
    setBrand((state) => ({ ...(state || {}), name } as Brand));

    if (typingTimeout) clearTimeout(typingTimeout);
    setTypingTimeout(setTimeout(() => getPreview({ ...brand, name }), 500));
  };

  const schema = yup.object().shape({
    name: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    code: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    status: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
  });

  const onValidateSchema = (data?: Brand) => {
    try {
      schema.validateSync(
        {
          name: data?.name,
          code: data?.code,
          status: data?.status,
        },
        { abortEarly: false }
      );
      setError([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setError(error?.inner);

      return false;
    }
  };

  const onSubmitData = () => {
    if (!onValidateSchema(brand)) return;
    if (brand) onSubmit(brand);
  };

  useImperativeHandle(ref, () => ({
    open: onOpenModal,
    close: onCloseModal,
  }));

  const getTitle = () => {
    if (view) {
      return translate("viewBrand");
    }
    if (brand?.id) {
      return translate("editBrand");
    }

    return translate("newBrand");
  };
  const onDeleteBrand = () => {
    if (brand?.id) {
      onDelete([brand?.id]);
    }
  };

  return (
    <Modal
      open={openModal}
      handleClose={onCloseModal}
      title={getTitle()}
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        dataCySave: "submitChange",
        onCancel: onCloseModal,
        saveDisabled: isLoading || view,
        cancelDisabled: isLoading || view,
        loading: isLoading,
        textSave: brand?.id ? translate("save") : translate("add"),
        onSave: onSubmitData,
      }}
      showLeftActionButtons
      actionLeftButtonProps={{
        dataCyCancel: "deleteButton",
        onCancel: brand?.id ? onDeleteBrand : undefined,
        textCancel: translate("delete"),
        cancelDisabled: isLoading || view,
      }}
      className={classes.modalWidth}
    >
      <section className={classes.sectionWidth}>
        <Grid container direction="row" justifyContent="space-between" spacing={2} xs={12}>
          <FieldsGrid
            item
            container
            direction="row"
            justifyContent="flex-start"
            spacing={2}
            xs={12}
            sm={12}
            md={12}
            lg={6}
          >
            <Grid item xs={12} sm={12}>
              <>
                <Label>{translate("logo")}</Label>
                <ImageInput
                  id="brand-logo"
                  title={""}
                  cropImage
                  cropAspectWidth={1024}
                  cropAspectHeight={320}
                  cropShape="rect"
                  logo={brand?.logo}
                  showActionButtons
                  disabled={view}
                  error={fieldHasError("logo")}
                  onChangeLogo={(logo?: Logo) => {
                    setBrand(
                      brand
                        ? {
                            ...brand,
                            logo,
                          }
                        : ({ logo } as Brand)
                    );
                  }}
                />
              </>
            </Grid>
            <Grid item xs={12}>
              <Input
                required
                name={"brandName"}
                disabled={view}
                label={translate("brandName")}
                placeholder=""
                value={brand?.name ?? ""}
                onChange={(event) => {
                  handleNameChange(event.target.value);
                }}
                error={fieldHasError("name")}
                errorMessage={getErrorByField("name")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input
                required
                disabled={view}
                label={translate("brandCode")}
                placeholder=""
                name={"brandCode"}
                value={brand?.code ?? ""}
                onChange={(event) => {
                  setBrand((state) => ({ ...(state || {}), code: event.target.value } as Brand));
                }}
                error={fieldHasError("code")}
                errorMessage={getErrorByField("code")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Select
                name="status"
                disabled={view}
                label={translate("status")}
                required
                value={brand?.status}
                error={fieldHasError("status")}
                errorMessage={getErrorByField("status")}
                onChange={(event) =>
                  setBrand((state) => ({ ...(state ?? {}), status: event.target.value } as Brand))
                }
              >
                <MenuItem value={Status.ACTIVE}>{translate("active")}</MenuItem>
                <MenuItem value={Status.INACTIVE}>{translate("inactive")}</MenuItem>
              </Select>
            </Grid>
          </FieldsGrid>
          <Grid item xs={12} sm={12} md={12} lg={6}>
            <PdfView buffer={buffer} loading={isLoadingPreview} />
          </Grid>
        </Grid>
      </section>
    </Modal>
  );
};

export default forwardRef(CreateModal);
